/**
 * The phone editor's chrome — `docs/property-maps-v2.md` §6.2, board 9.
 *
 * **The map gets the whole screen.** The Property header, its tab strip and
 * the bottom bar are all gone; what is left floats over the canvas as white
 * pills. `100dvh` rather than `100vh`, because `vh` on iOS is the height the
 * screen would have without the browser's own chrome and a dock measured
 * against it sits under the address bar.
 *
 * **Nothing here holds state.** The editor already owns the tool, the active
 * layer, the camera and the save; this is where those are drawn on a phone,
 * the way `MapBar` and the rail are where they are drawn on a desktop. One
 * editor, two shells — which is the only way the two surfaces can be made to
 * keep saying the same thing.
 */
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { Segmented } from '../../components/primitives.js'
import { PanelSheet, SheetButton } from '../../components/panel-sheet.js'
import {
  IconChevronDown, IconChevronLeft, IconLayers, IconLink, IconLocate, IconMinus, IconMore,
  IconPlus, IconRuler, IconUndo,
} from '../../frame/icons.js'
import type { MapBaseKind } from '../../lib/map-provider.js'

const BASES: { value: MapBaseKind; label: string }[] = [
  { value: 'satellite', label: 'Satellite' },
  { value: 'map', label: 'Map' },
  { value: 'hybrid', label: 'Hybrid' },
]

export function PhoneEditorShell({
  mapName, measuring, layerCount, activeChip, saveDot, onBack, onSwitch, onLayers,
  onZoomIn, onZoomOut, onHome, children,
}: {
  mapName: string
  measuring: boolean
  layerCount: number
  /** `Drawing on <layer> ▾`, the editor's own chip at touch size. */
  activeChip?: ReactNode
  /** The status pill, collapsed to a dot at 390px (§6.2). */
  saveDot?: ReactNode
  onBack: () => void
  onSwitch: () => void
  onLayers: () => void
  onZoomIn: () => void
  onZoomOut: () => void
  onHome: () => void
  /** The `⋯` button, which the editor owns because its sheet is its menu. */
  children?: ReactNode
}) {
  return (
    <>
      {/* Top row: out, which map, and everything else. */}
      <div className="pointer-events-auto absolute inset-x-3 top-3 flex items-start gap-2">
        <RoundButton label="Back" onClick={onBack}>
          <IconChevronLeft size={19} />
        </RoundButton>

        <button
          type="button"
          onClick={onSwitch}
          className={cn(
            'flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-button px-3',
            'text-cell font-bold shadow-popover',
            measuring ? 'bg-warn-bg text-warn' : 'bg-surface text-text',
          )}
        >
          <span className="truncate">
            {measuring ? 'Measuring · not a map' : mapName}
          </span>
          {!measuring && <IconChevronDown size={14} className="shrink-0 text-text-faint" />}
        </button>

        {children}
      </div>

      {/* Second row: what is being drawn on, how many layers, how it saved. */}
      <div className="pointer-events-auto absolute inset-x-3 top-[62px] flex items-center gap-2">
        {activeChip}
        <button
          type="button"
          onClick={onLayers}
          /* §7: 44px, like every other control a thumb has to find on a
             map — it sat at 36 because it was written beside the chip, which
             is a label with a menu rather than a target. */
          className={cn(
            'inline-flex h-11 shrink-0 items-center gap-1.5 rounded-button bg-surface px-3',
            'text-sub font-semibold shadow-popover',
          )}
        >
          <IconLayers size={16} />
          {layerCount}
        </button>
        {saveDot}
      </div>

      {/* Right edge: the camera, at 44px because a thumb is not a pointer. */}
      <div className="pointer-events-auto absolute right-3 top-[110px] flex flex-col gap-2">
        <RoundButton label="Zoom in" onClick={onZoomIn}><IconPlus size={18} /></RoundButton>
        <RoundButton label="Zoom out" onClick={onZoomOut}><IconMinus size={18} /></RoundButton>
        <RoundButton label="Back to the property" onClick={onHome}>
          <IconLocate size={18} />
        </RoundButton>
      </div>
    </>
  )
}

function RoundButton({ label, onClick, children }: {
  label: string; onClick: () => void; children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface',
        'text-text-body shadow-popover',
      )}
    >
      {children}
    </button>
  )
}

/** The `⋯` button itself, so the row's three children stay one line. */
export function MoreButton({ onClick }: { onClick: () => void }) {
  return (
    <RoundButton label="More" onClick={onClick}><IconMore size={19} /></RoundButton>
  )
}

/**
 * What `⋯` opens — §6.2's list.
 *
 * Everything a desktop keeps on screen at all times and a phone has no room
 * for: the base, sharing, print, units and undo. It is a sheet rather than a
 * menu because a 52px row is the one thing a thumb hits reliably, and
 * `PanelSheet` is already that row everywhere else in the product.
 */
export function PhoneMoreSheet({
  base, onBase, units, onUnits, canUndo, onUndo, onMeasurements, onSharing, onPrint, onClose,
}: {
  base: MapBaseKind
  onBase: (base: MapBaseKind) => void
  /** Area units, display only — the same preference the measure popup keeps. */
  units: 'sq ft' | 'sq yd' | 'ac'
  onUnits: (units: 'sq ft' | 'sq yd' | 'ac') => void
  canUndo: boolean
  onUndo: () => void
  /**
   * §6.4's Measurements sheet, where the `measurements` feature is on.
   *
   * A desktop reaches it from the panel's tab row, which a phone does not
   * have — so without a row here a phone could measure a property and have
   * nowhere to read back what it measured.
   */
  onMeasurements?: () => void
  onSharing?: () => void
  onPrint?: () => void
  onClose: () => void
}) {
  return (
    <PanelSheet
      open
      onClose={onClose}
      title="This map"
      footer={<SheetButton onClick={onClose}>Done</SheetButton>}
    >
      <Row label="Base">
        <Segmented options={BASES} value={base} onChange={onBase} label="Base" />
      </Row>
      <Row label="Units">
        <Segmented
          options={[
            { value: 'sq ft' as const, label: 'sq ft' },
            { value: 'sq yd' as const, label: 'sq yd' },
            { value: 'ac' as const, label: 'ac' },
          ]}
          value={units}
          onChange={onUnits}
          label="Units"
        />
      </Row>

      <SheetRow
        label="Undo the last change"
        icon={<IconUndo size={15} />}
        disabled={!canUndo}
        onClick={() => { onUndo(); onClose() }}
      />
      {onMeasurements && (
        <SheetRow
          label="Measurements"
          icon={<IconRuler size={15} />}
          onClick={() => { onClose(); onMeasurements() }}
        />
      )}
      {onSharing && (
        <SheetRow
          label="Sharing"
          icon={<IconLink size={15} />}
          onClick={() => { onClose(); onSharing() }}
        />
      )}
      {/*
        * Print is §9.6's page and arrives with it. The row is absent rather
        * than greyed until then — a control that opens nothing is worse than
        * one somebody has not found yet.
        */}
      {onPrint && <SheetRow label="Print / PDF" onClick={() => { onClose(); onPrint() }} />}
    </PanelSheet>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-[52px] items-center gap-3 border-b border-line-faint px-4">
      <span className="shrink-0 text-body font-bold">{label}</span>
      <span className="ml-auto">{children}</span>
    </div>
  )
}

function SheetRow({ label, icon, disabled, onClick }: {
  label: string; icon?: ReactNode; disabled?: boolean; onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex min-h-[52px] w-full items-center gap-2 border-b border-line-faint px-4',
        'text-left text-body font-bold disabled:opacity-40',
      )}
    >
      {icon && <span className="text-text-muted">{icon}</span>}
      {label}
    </button>
  )
}
